import { useState, useEffect } from "react";
import Button from "../../../components/common/button";
import { EditSvg, LeftArrowSvg, RightArrowSvg, TrashSvg } from "../../../icons";
import Switch from "../../../components/common/Switch";
import { FormProvider, useForm } from "react-hook-form";
import { IVideos } from "../../../utils/types";
import AddItemModal from "./AddItemModal";
import useUpdateCategoryComponentVisiblity from "../../../hooks/use-update-category-component-visiblity";
import useDeleteCategoryComponent from "../../../hooks/use-delete-categor-component";
import { toast } from "react-toastify";
import Modal from "../../../components/common/Modal";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DraggableRow from "../../../components/common/DragRow";
import useUpdateComponentViewRank from "../../../hooks/use-update-component-view-rank";
import Spinner from "../../../components/common/Spinner";

type IFormData = {
    videos: IVideos[];
};
const DynamicViewLayout = ({ componentName, componentData, allComponentsData, refetchComponents }: { componentName: string, componentData?: any, allComponentsData?: any, refetchComponents?: () => void }) => {
    const [currentData, setCurrentData] = useState(componentData);
    const [currentName, setCurrentName] = useState(componentName);
    const [navigationStack, setNavigationStack] = useState<{ data: any, name: string }[]>([]);
    const [currentCategory, setCurrentCategory] = useState<string>(''); // Track current category for adding items
    const methods = useForm<IFormData>({ defaultValues: { videos: [] } });

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [editItem, setEditItem] = useState<any>(null);
    const [deleteItem, setDeleteItem] = useState<any>(null);


    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const openModal = () => setIsModalOpen(true);

    const { mutate: updateVisibility, isPending: updateVisibilityLoading } = useUpdateCategoryComponentVisiblity();
    const { mutate: deleteComponent, isPending: deleteComponentLoading } = useDeleteCategoryComponent();
    const { mutate: updateViewRank, isPending: updateViewRankLoading } = useUpdateComponentViewRank();

    const closeModal = () => {
        setIsModalOpen(false);
        setEditItem(null);
        setIsEditMode(false);
    };

    useEffect(() => {
        setCurrentData(componentData);
        setCurrentName(componentName);
        setNavigationStack([]);
    }, [componentData, componentName]);

    if (!currentData || !Array.isArray(currentData)) {
        return <div>{currentName}</div>;
    }

    // const checkListExists = (item: any) => {
    //     return item.goTo && allComponentsData?.component[item.goTo]
    // }

    const handleItemSuccess = (itemData: any, isEdit: boolean) => {
        if (refetchComponents) {
            refetchComponents();
        }
        
        if (isEdit) {
            setCurrentData((prev: any) =>
                prev.map((item: any) =>
                    item._id === itemData._id ? itemData : item
                )
            );

            // Update in allComponentsData if in submenu
            if (currentCategory && allComponentsData?.component?.[currentCategory]) {
                const index = allComponentsData.component[currentCategory].findIndex((item: any) => item._id === itemData._id);
                if (index !== -1) {
                    allComponentsData.component[currentCategory][index] = itemData;
                }
            }
        } else {
            setCurrentData((prev: any) => [...(prev || []), itemData]);

            // If we're in a submenu, also update the allComponentsData
            if (currentCategory && allComponentsData?.component) {
                if (!allComponentsData.component[currentCategory]) {
                    allComponentsData.component[currentCategory] = [];
                }
                allComponentsData.component[currentCategory].push(itemData);
            }
        }
    };

    const handleSubmenuClick = (item: any) => {
        setNavigationStack(prev => [...prev, { data: currentData, name: currentName }]);

        if (item.goTo && allComponentsData?.component[item.goTo]) {
            setCurrentData(allComponentsData.component[item.goTo]);
        } else {
            setCurrentData([]); // Empty data if no submenu exists
        }

        setCurrentName(item.name);
        setCurrentCategory(item.goTo || item.name); // Set category for new items
    };

    const handleEditItem = (item: any) => {
        setEditItem(item);
        setIsEditMode(true);
        setIsModalOpen(true);
    };

    const handleItemVisibilityToggle = (item: any) => {
        updateVisibility({ _id: item._id, isVisible: !item.isVisible }, {
            onSuccess: (res: any) => {
                // Toggle the inactive status
                const updatedItem = { ...item, isVisible: !item.isVisible };
                // Update currentData state
                setCurrentData((prev: any) =>
                    prev.map((dataItem: any) =>
                        dataItem._id === item._id ? updatedItem : dataItem
                    )
                );

                // If we're in a submenu, also update the allComponentsData
                if (currentCategory && allComponentsData?.component?.[currentCategory]) {
                    const index = allComponentsData.component[currentCategory].findIndex((dataItem: any) => dataItem._id === item._id);
                    if (index !== -1) {
                        allComponentsData.component[currentCategory][index] = updatedItem;
                    }
                }
                toast.success(res?.message || "Component visibility updated successfully");
            },
            onError: (error: any) => { console.error(error?.response?.data?.message || "Error updating visibility:", error); }
        });

    };

    const handleDeleteItem = (item: any) => {
        deleteComponent({ _id: item._id, url: item.viewType !== 'submenu' ? 'v1/delete-category-component' : '/deleteCategoryComponent' }, {
            onSuccess: (res: any) => {
                // Remove from currentData state
                setCurrentData((prev: any) =>
                    prev.filter((dataItem: any) => dataItem._id !== item._id)
                );

                // If we're in a submenu, also update the allComponentsData
                if (currentCategory && allComponentsData?.component?.[currentCategory]) {
                    allComponentsData.component[currentCategory] = allComponentsData.component[currentCategory].filter((dataItem: any) => dataItem._id !== item._id);
                }

                toast.success(res?.message || "Component deleted successfully");
                setDeleteModalOpen(false);
                setDeleteItem(null);
            },
            onError: (error: any) => { console.error(error?.response?.data?.message || "Error deleting component:", error); }
        });
    };
    const handleBack = () => {
        if (navigationStack.length > 0) {
            const previous = navigationStack[navigationStack.length - 1];
            setCurrentData(previous.data);
            setCurrentName(previous.name);
            setNavigationStack(prev => prev.slice(0, -1));
        }
    };

    const moveRow = (dragIndex: number, hoverIndex: number) => {
        const dragItem = currentData[dragIndex];
        const updatedData = [...currentData];
        updatedData.splice(dragIndex, 1);
        updatedData.splice(hoverIndex, 0, dragItem);

        // Update viewRank for all items based on new positions
        const reorderedData = updatedData.map((item, index) => ({
            ...item,
            viewRank: index + 1
        }));

        setCurrentData(reorderedData);
    }

    const onDropComplete = () => {
        const updatedItems = [...currentData].map((item, index) => ({
            ...item,
            viewRank: index + 1,
        }));

        setCurrentData(
            updatedItems.sort((a, b) => a?.viewRank - b?.viewRank)
        );

        // Here you can also send the updated order to the backend if needed
        updateViewRank({ data: updatedItems.map((item: any) => ({ id: item?._id, viewRank: item?.viewRank })) }, {
            onSuccess: (res: any) => {
                toast.success(res?.message || 'Component order updated successfully');
            },
            onError: (error: any) => {
                console.error(error?.response?.data?.message || "Error updating component order:", error);
            }
        })
    }

    return (
        <div className="w-full mt-6 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {navigationStack.length > 0 && (
                        <Button variant="default" onClick={handleBack} className="py-1 px-2 border">
                            <LeftArrowSvg className="" /> Back
                        </Button>
                    )}
                    <div className="flex items-center gap-1">

                        <h4 className="text-lg font-semibold text-[#111827]">
                            {currentName}
                        </h4>
                    </div>
                </div>
                <Button variant="primary" onClick={openModal}>Add New</Button>
            </div>


            {/* Headers Only */}
            <div className="grid grid-cols-12 px-0 text-sm font-medium text-gray-500">
                <div className="col-span-10" >{navigationStack.map((nav, index) => (
                    <span key={index} className="text-sm text-gray-500">
                        {nav.name} &gt; &nbsp;
                    </span>
                ))}</div>
                <div className="col-span-1 text-right">Enable/Disable</div>
                <div className="col-span-1 text-center">Action</div>
            </div>

            {/* Rows */}
            <div className="space-y-3">
                <DndProvider backend={HTML5Backend}>
                    {updateViewRankLoading || updateVisibilityLoading ? (
                        <Spinner fullScreenOverlay className="z-[100]" />
                    ) : null}
                    {currentData.sort((a: any, b: any) => a.viewRank - b.viewRank).map((item: any, index: number) => {
                        const hideViewTypes = [
                            'button',
                            'dropdown',
                            'fixedbutton',
                            'incrementwithimagewithoutrequest',
                            'laundry',
                            'submenuwithcart'
                        ];

                        if (hideViewTypes.includes(item.viewType)) return null;

                        const quantity = 0;

                        return (
                            <FormProvider {...methods} key={item._id}>
                                <DraggableRow
                                    index={index}
                                    moveRow={moveRow}
                                    onDropComplete={onDropComplete}
                                    custumClassName="w-full h-full cursor-grab items-center"
                                >
                                    <div
                                        className="grid grid-cols-12 gap-1
                                        bg-white border rounded-lg
                                        hover:border-gray-300 hover:shadow-sm
                                        transition"
                                    >
                                        {/* Main Content (NO HEADER) */}
                                        <div className="col-span-10 p-4">
                                            <div className="w-2/3 border p-4 rounded-sm shadow-md">

                                                {item.viewType === 'webView' ? (
                                                    <div
                                                        className="cursor-pointer"
                                                        onClick={() => window.open(item.link, '_blank')}
                                                    >
                                                        <h4 className="font-medium text-[#666672]">
                                                            {item.title}
                                                        </h4>
                                                        <p className="text-sm text-gray-400">
                                                            {item.subtitle}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-between gap-4">
                                                        <h4 className="font-medium text-[#666672]">
                                                            {item.name}
                                                        </h4>

                                                        <div className="flex items-center gap-2">
                                                            {item.viewType === 'increment' && (
                                                                <div className="flex items-center gap-1">
                                                                    <Button variant="default" >−</Button>
                                                                    <span className="px-3 py-1 border rounded text-sm">
                                                                        {quantity}
                                                                    </span>
                                                                    <Button variant="default" >+</Button>
                                                                </div>
                                                            )}

                                                            {(item.viewType === 'button' ||
                                                                item.viewType === 'textbox' ||
                                                                item.viewType === 'text' ||
                                                                item.viewType === 'increment') && (
                                                                    <Button variant="default" >
                                                                        Request
                                                                    </Button>
                                                                )}

                                                            {item.viewType === 'submenu' && (
                                                                <RightArrowSvg className="text-black cursor-pointer" onClick={() => { handleSubmenuClick(item) }} />
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {item.viewType === 'textbox' && (
                                                    <textarea
                                                        className="
                                                mt-2 w-full p-2 border rounded
                                                text-sm text-gray-600
                                                focus:outline-none focus:ring-1 focus:ring-primary
                                                "
                                                        placeholder={item.description || 'Enter text'}
                                                        disabled
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Enable / Disable Column */}
                                        <div className="col-span-1 flex justify-center items-center  px-1">
                                            <Switch
                                                name={`switch-${item._id}`}
                                                checked={item?.isVisible || false}
                                                onCheck={() => { handleItemVisibilityToggle(item) }}
                                                disabled={updateVisibilityLoading}
                                            />
                                        </div>

                                        {/* Action Column */}
                                        <div className="col-span-1 flex justify-center items-center gap-1 ">
                                            {/* Edit */}
                                            <div
                                                className="h-8 w-8 rounded-full bg-[#EFF6FF] flex items-center justify-center hover:bg-[#DBEAFE] transition-colors cursor-pointer"
                                                onClick={() => handleEditItem(item)}
                                            >
                                                <EditSvg color="#2563EB" />
                                            </div>

                                            {/* Delete */}
                                            <div
                                                className="h-8 w-8 rounded-full bg-[#FFF1F2] flex items-center justify-center hover:bg-[#FFE4E6] transition-colors cursor-pointer"
                                                onClick={() => { setDeleteItem(item); setDeleteModalOpen(true); }}
                                            >
                                                <TrashSvg color="#E52727" />
                                            </div>
                                        </div>
                                    </div>
                                </DraggableRow>
                            </FormProvider>
                        );
                    })}
                </DndProvider>
            </div>

            <AddItemModal
                isOpen={isModalOpen}
                closeModal={closeModal}
                onSuccess={handleItemSuccess}
                itemData={editItem}
                isEditItem={isEditMode}
                currentListLength={currentData?.length || 0}
                componentName={componentName}
                allComponentsData={allComponentsData}
                currentName={currentName}
            />
            <Modal
                isOpen={deleteModalOpen}
                closeModal={() => { setDeleteModalOpen(false); setDeleteItem(null); }}
                title="Are you sure you want to delete this item?"
                className="w-144.75"
            >
                <div className="flex items-center justify-end gap-[18px] mt-9">
                    <Button className="w-25.5" onClick={() => { setDeleteModalOpen(false); setDeleteItem(null); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => { handleDeleteItem(deleteItem) }}
                        loading={deleteComponentLoading}
                        disabled={deleteComponentLoading}
                        className="w-25.5"
                        variant="primary"
                    >
                        Delete
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default DynamicViewLayout;
